import dayjs from "dayjs";
import "dayjs/locale/pl";
import { resolvePlurals } from "@/service/text/plurals";

dayjs.locale("pl");

// dayjs (pl) odmienia miesiąc tylko w parze z dniem ("8 sierpnia"),
// a sam "MMMM" zwraca mianownik — stąd własne mapy przypadków.
const WEEKDAY_ACCUSATIVE = [
  "niedzielę",
  "poniedziałek",
  "wtorek",
  "środę",
  "czwartek",
  "piątek",
  "sobotę",
];

const WEEKDAY_PLURAL = [
  "niedziele",
  "poniedziałki",
  "wtorki",
  "środy",
  "czwartki",
  "piątki",
  "soboty",
];

const MONTH_GENITIVE = [
  "stycznia",
  "lutego",
  "marca",
  "kwietnia",
  "maja",
  "czerwca",
  "lipca",
  "sierpnia",
  "września",
  "października",
  "listopada",
  "grudnia",
];

/**
 * Wyciąga dane opisowe najbliższej niezakończonej rundy próbnego LEK-u
 * (grupa egzaminów z `exam.practice_exam.status`), żeby nie trzymać
 * dat i godzin na sztywno w tekstach.
 */
export const getPracticeExamInfo = ({
  startDate,
  endDate,
  positionInGroup,
  groupSize,
}: ExamPracticeExamStatusVM) => {
  if (!startDate || !endDate) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const roundsCount = groupSize ?? null;
  const weekdayPlural = WEEKDAY_PLURAL[start.day()];
  const monthGenitive = MONTH_GENITIVE[start.month()];

  // Rundy idą co tydzień o tej samej godzinie (stąd „co sobotę" w opisie edycji), więc zakres
  // całej edycji da się odtworzyć z pozycji bieżącej rundy — backend nie podaje dat pozostałych.
  const currentRound = positionInGroup ?? 1;
  const firstRoundStart = start.subtract(currentRound - 1, "week");
  const lastRoundStart = start.add(
    (roundsCount ?? currentRound) - currentRound,
    "week",
  );
  const isSameMonthRange = firstRoundStart.month() === lastRoundStart.month();

  return {
    roundsCount,
    isFirstRound: positionInGroup === 1,
    startTime: start.format("HH:mm"),
    endTime: end.format("HH:mm"),
    roundDate: start.format("D MMMM"),
    roundDateTime: start.format("D MMMM o HH:mm"),
    // "8.08 o 10:00" — krótka forma do nagłówka banera, gdzie nie ma miejsca na nazwę miesiąca
    roundDateTimeShort: start.format("D.MM [o] HH:mm"),
    weekdayAccusative: WEEKDAY_ACCUSATIVE[start.day()],
    weekdayPlural,
    monthGenitive,
    // "4 rundy" / "1 rundę" / "5 rund"
    roundsLabel: roundsCount
      ? resolvePlurals(roundsCount, "rundę", "rund", "rundy")
      : null,
    // "8–29 sierpnia o 10:00" — zakres dat całej edycji; przy jednej rundzie to zwykła data
    roundsRangeLabel:
      !roundsCount || roundsCount < 2
        ? start.format("D MMMM o HH:mm")
        : isSameMonthRange
        ? `${firstRoundStart.date()}–${lastRoundStart.date()} ${
            MONTH_GENITIVE[lastRoundStart.month()]
          } o ${start.format("HH:mm")}`
        : `${firstRoundStart.date()} ${
            MONTH_GENITIVE[firstRoundStart.month()]
          } – ${lastRoundStart.date()} ${
            MONTH_GENITIVE[lastRoundStart.month()]
          } o ${start.format("HH:mm")}`,
  };
};

export type PracticeExamInfo = NonNullable<
  ReturnType<typeof getPracticeExamInfo>
>;

/**
 * Podpis edycji nad sekcją kart rund: „4 rundy, co sobotę o 10:00 (8–29 sierpnia)".
 * Cykl (dzień, godzina) bierze z pierwszej rundy, a zakres dat z pierwszej i ostatniej.
 */
export const getPracticeExamRoundsSummary = (
  rounds: ExamPracticeExamRoundVM[],
): { label: string; dateRange: string } | null => {
  if (rounds.length < 1) return null;

  const first = dayjs(rounds[0].startDate);
  const last = dayjs(rounds[rounds.length - 1].startDate);

  const roundsLabel = resolvePlurals(rounds.length, "runda", "rund", "rundy");
  const weekdayAccusative = WEEKDAY_ACCUSATIVE[first.day()];
  const startTime = first.format("HH:mm");

  const dateRange =
    first.month() === last.month()
      ? `${first.date()}–${last.date()} ${MONTH_GENITIVE[first.month()]}`
      : `${first.date()} ${MONTH_GENITIVE[first.month()]} – ${last.date()} ${
          MONTH_GENITIVE[last.month()]
        }`;

  return {
    label: `${roundsLabel}, co ${weekdayAccusative} o ${startTime}`,
    dateRange,
  };
};

// „1:20:02" — czas podejścia (godziny bez wiodącego zera, minuty i sekundy dwucyfrowe)
export const formatPracticeRoundTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;

  return `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(
    2,
    "0",
  )}`;
};

// „29 sierpnia o 10:00" — start nadchodzącej rundy
export const formatPracticeRoundStart = (date: DateString): string =>
  dayjs(date).format("D MMMM [o] HH:mm");
